import SendIcon from "@mui/icons-material/Send";
import {
  CircularProgress,
  Divider,
  IconButton,
  List,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import { enqueueSnackbar } from "notistack";
import React, { ReactElement, useContext, useEffect, useState } from "react";

import { EditComment } from "../../../components/comments/parts/edit-comment";
import { ListCommentItem } from "../../../components/comments/parts/list-comment-item";
import { ReplyComment } from "../../../components/comments/parts/reply-comment";
import { SubComments } from "../../../components/comments/parts/sub-comments";
import { Comment } from "../../../generated/gql/graphql";
import useAssetCommentMutation from "../../../hooks/mutation/comments/use-asset-comment-mutation";
import useAssetComments from "../../../hooks/query/comments/use-asset-comments";
import useText from "../../../hooks/use-text";
import { UserContext } from "../../../providers/user-provider";
import { parseType } from "../../../utils/graphql";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  assetId: string;
};

export default function ActivityFeed(props: Props): ReactElement {
  const { assetId } = props;

  const { strings } = useText(local);

  const [editCommentId, setEditCommentId] = useState<string>();
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState<string>();

  const { me } = useContext(UserContext);
  const { createComment, deleteComment, updateComment } =
    useAssetCommentMutation();

  const { data, loading, error } = useAssetComments({
    filter: {
      assetId: {
        eq: assetId,
      },
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    setComments(parseType<Comment[]>("comments", data));
  }, [data]);

  async function internalAddComment(
    comment: string,
    parentCommentId?: string
  ): Promise<void> {
    if (!comment) {
      return;
    }

    try {
      const createdComment = await createComment({
        assetId: assetId,
        comment: comment,
        parentId: parentCommentId,
      });

      setComments((prev) => prev.concat([createdComment]));
      setCommentText("");
    } catch (error) {
      enqueueSnackbar(strings.errors.failedCreation, { variant: "error" });
      return;
    }
  }

  async function internalDeleteComment(id: string): Promise<void> {
    try {
      await deleteComment(id);
      setComments((prev) => prev.filter((comment) => comment.id !== id));
    } catch (error) {
      enqueueSnackbar(strings.errors.failedDeletion, { variant: "error" });
    }
  }

  async function internalEditComment(id: string, value: string): Promise<void> {
    try {
      await updateComment(id, {
        comment: value,
      });

      const updatedComments = comments.map((v) => {
        if (v.id !== id) {
          return v;
        }

        return {
          ...v,
          comment: value,
        };
      });

      setComments(updatedComments);
      setEditCommentId(undefined);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.errors.failedUpdating, { variant: "error" });
    }
  }

  return (
    <div>
      <List className="pt-0 w-full p-0 mb-[-1px]">
        {loading && (
          <Box className="text-center">
            <CircularProgress />
          </Box>
        )}
        {error && <div>{strings.errors.general}</div>}
        {comments && comments.length === 0 && (
          <Box className="text-center">
            <p>{strings.empty}</p>
          </Box>
        )}
        {comments &&
          comments.map(
            (comment) =>
              !comment.parentId && (
                <div key={comment.dateCreated}>
                  {editCommentId === comment.id ? (
                    <EditComment
                      comment={comment}
                      editCommentId={editCommentId}
                      handleEditComment={internalEditComment}
                    />
                  ) : (
                    <ListCommentItem
                      comment={comment}
                      setEditCommentId={setEditCommentId}
                      editable={me?.id === comment.userId}
                      handleRemoveComment={internalDeleteComment}
                    />
                  )}
                  <SubComments
                    removeComment={internalDeleteComment}
                    commentId={comment.id}
                    comments={comments.filter((v) => v.parentId === comment.id)}
                    editCommentId={editCommentId}
                    handleEditComment={(id, comment) =>
                      internalAddComment(comment, id)
                    }
                    setEditCommentId={setEditCommentId}
                  />
                  <ReplyComment comment={comment} />
                  <Divider className="mx-5" />
                </div>
              )
          )}
      </List>
      <div className="flex">
        <TextField
          onChange={(e) => setCommentText(e.target.value)}
          label="Leave a comment"
          placeholder="Type something here…"
          className="w-full"
          size="small"
        />
        <IconButton
          onClick={() => internalAddComment(commentText || "")}
          className="mt-[-1px] !bg-white rounded-none"
          disabled={!commentText}
          size="medium"
        >
          <SendIcon fontSize="inherit" />
        </IconButton>
      </div>
    </div>
  );
}

type LocalSchema = {
  empty: string;
  errors: {
    general: string;
    failedCreation: string;
    failedUpdating: string;
    failedDeletion: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    empty: "No comments yet.",
    errors: {
      general: "Something went wrong. Please retry it again.",
      failedCreation: "Failed to create comment",
      failedUpdating: "Failed to update comment",
      failedDeletion: "Failed to delete comment",
    },
  },
};
